const PLACEHOLDER_SECTOR_KEYS = new Set([
    '',
    'null',
    'nulo',
    'undefined',
    'none',
    'nan',
]);

const SECTOR_CONNECTOR_TOKENS = new Set([
    'a',
    'an',
    'and',
    'as',
    'da',
    'das',
    'de',
    'do',
    'dos',
    'e',
    'em',
    'of',
    'the',
]);

const TOKEN_STEM_RULES = [
    { stem: 'aeronautica', pattern: /^aeronaut|^aeroe?spaci|^aviac/ },
    { stem: 'agronegocio', pattern: /^agronegoci|^agropecuari/ },
    { stem: 'ambiente', pattern: /^ambient/ },
    { stem: 'automotivo', pattern: /^automot|^automobil|^veicul/ },
    { stem: 'energia', pattern: /^energia$|^energy$/ },
    { stem: 'saude', pattern: /^saude$|^health$/ },
    { stem: 'tecnologia', pattern: /^tecnolog|^technology$/ },
    { stem: 'mineracao', pattern: /^miner(a|i)/ },
    { stem: 'industria', pattern: /^industri/ },
    { stem: 'commodity', pattern: /^commodit/ },
    { stem: 'cripto', pattern: /^cripto|^crypto$/ },
    { stem: 'dividendo', pattern: /^dividend/ },
    { stem: 'financeiro', pattern: /^financ|^financial$/ },
    { stem: 'fundo', pattern: /^fundo?s?$|^funds?$/ },
    { stem: 'investimento', pattern: /^investimentos?$/ },
    { stem: 'servico', pattern: /^servic/ },
    { stem: 'seguro', pattern: /^seguros?$/ },
    { stem: 'transporte', pattern: /^transport/ },
    { stem: 'cultura', pattern: /^cultur/ },
    { stem: 'juridico', pattern: /^juridic|^judiciari|^judicial$|^justica$|^legal$/ },
    { stem: 'private', pattern: /^private$/ },
    { stem: 'mercado', pattern: /^mercado$/ },
    { stem: 'market', pattern: /^markets?$/ },
    { stem: 'stock', pattern: /^stocks?$/ },
    { stem: 'digital', pattern: /^digital$/ },
    { stem: 'asset', pattern: /^assets?$/ },
    { stem: 'etf', pattern: /^etfs?$/ },
];

const hasStem = (profile, stem) => profile.stemSet.has(stem);

const hasAnyStem = (profile, stems) => stems.some((stem) => hasStem(profile, stem));

const hasOnlyStems = (profile, stems) => (
    profile.stems.length > 0 && profile.stems.every((stem) => stems.includes(stem))
);

const matchesSingleStem = (stem) => (profile) => hasOnlyStems(profile, [stem]);

const SEMANTIC_SECTOR_GROUPS = [
    {
        key: 'mercado-financeiro',
        label: 'Mercado Financeiro',
        match: (profile) => (
            hasStem(profile, 'financeiro')
            && hasAnyStem(profile, ['mercado', 'market'])
            && hasOnlyStems(profile, ['financeiro', 'mercado', 'market'])
        ),
    },
    {
        key: 'private-markets',
        label: 'Private Markets',
        match: (profile) => (
            hasStem(profile, 'private')
            && hasStem(profile, 'market')
            && hasOnlyStems(profile, ['private', 'market'])
        ),
    },
    {
        key: 'mercado',
        label: 'Mercado',
        match: (profile) => (
            hasAnyStem(profile, ['mercado', 'market', 'stock'])
            && hasOnlyStems(profile, ['mercado', 'market', 'stock'])
        ),
    },
    {
        key: 'cripto',
        label: 'Cripto',
        match: (profile) => (
            hasStem(profile, 'cripto')
            && hasOnlyStems(profile, ['cripto', 'digital', 'asset'])
        ),
    },
    {
        key: 'fundo',
        label: 'Fundo',
        match: (profile) => (
            hasStem(profile, 'fundo')
            && hasOnlyStems(profile, ['fundo', 'etf'])
        ),
    },
    { key: 'aeronautica', label: 'Aeronáutica', match: matchesSingleStem('aeronautica') },
    { key: 'agronegocio', label: 'Agronegócio', match: matchesSingleStem('agronegocio') },
    {
        key: 'ambiente',
        label: 'Meio Ambiente',
        match: (profile) => (
            hasStem(profile, 'ambiente')
            && hasOnlyStems(profile, ['ambiente', 'meio'])
        ),
    },
    { key: 'automotivo', label: 'Automotivo', match: matchesSingleStem('automotivo') },
    { key: 'energia', label: 'Energia', match: matchesSingleStem('energia') },
    { key: 'investimento', label: 'Investimento', match: matchesSingleStem('investimento') },
    { key: 'saude', label: 'Saúde', match: matchesSingleStem('saude') },
    { key: 'tecnologia', label: 'Tecnologia', match: matchesSingleStem('tecnologia') },
    { key: 'mineracao', label: 'Mineração', match: matchesSingleStem('mineracao') },
    { key: 'industria', label: 'Indústria', match: matchesSingleStem('industria') },
    { key: 'commodity', label: 'Commodity', match: matchesSingleStem('commodity') },
    { key: 'dividendo', label: 'Dividendo', match: matchesSingleStem('dividendo') },
    { key: 'financeiro', label: 'Financeiro', match: matchesSingleStem('financeiro') },
    { key: 'servico', label: 'Serviço', match: matchesSingleStem('servico') },
    { key: 'seguro', label: 'Seguro', match: matchesSingleStem('seguro') },
    { key: 'transporte', label: 'Transporte', match: matchesSingleStem('transporte') },
    { key: 'cultura', label: 'Cultura', match: matchesSingleStem('cultura') },
    { key: 'juridico', label: 'Jurídico', match: matchesSingleStem('juridico') },
];

const toText = (value) => (value == null ? '' : String(value));

const normalizeDisplayText = (value) => toText(value).trim().replace(/\s+/g, ' ');

const getOptionValue = (option) => {
    if (option && typeof option === 'object') {
        return normalizeDisplayText(option.value ?? option.text);
    }
    return normalizeDisplayText(option);
};

const getOptionText = (option) => {
    if (option && typeof option === 'object') {
        return normalizeDisplayText(option.text ?? option.value);
    }
    return normalizeDisplayText(option);
};

export const normalizeSectorFilterLabel = (value) => {
    const text = normalizeDisplayText(value);
    if (!text) return '';

    return text
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/&/g, ' e ')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
};

export const isPlaceholderSector = (value) => PLACEHOLDER_SECTOR_KEYS.has(normalizeSectorFilterLabel(value));

const normalizeTokenStem = (token) => {
    const rule = TOKEN_STEM_RULES.find(({ pattern }) => pattern.test(token));
    return rule?.stem ?? token;
};

const getSectorProfile = (value) => {
    const normalized = normalizeSectorFilterLabel(value);
    const tokens = normalized
        ? normalized.split(' ').filter((token) => token && !SECTOR_CONNECTOR_TOKENS.has(token))
        : [];
    const stems = tokens.map(normalizeTokenStem);

    return {
        normalized,
        tokens,
        stems,
        stemSet: new Set(stems),
    };
};

const getCanonicalSector = (value) => {
    const profile = getSectorProfile(value);
    if (!profile.normalized) return { key: '', label: '' };

    const group = SEMANTIC_SECTOR_GROUPS.find(({ match }) => match(profile));
    if (group) return { key: group.key, label: group.label, fixedLabel: true };

    return { key: profile.normalized, label: normalizeDisplayText(value), fixedLabel: false };
};

const addUnique = (values, value) => {
    if (!value || values.includes(value)) return;
    values.push(value);
};

const getDisplayLabelRank = (value) => {
    const label = normalizeDisplayText(value);
    if (!label) return -1;

    let rank = 0;
    if (/^[A-ZÀ-Ý]/.test(label)) rank += 3;
    if (/[À-ỹ]/.test(label)) rank += 2;
    if (label === label.toUpperCase() && /[A-ZÀ-Ý]/.test(label)) rank -= 2;
    if (label === label.toLowerCase() && /[a-zà-ÿ]/.test(label)) rank -= 1;
    return rank;
};

const chooseDisplayLabel = (current, next) => {
    const currentRank = getDisplayLabelRank(current);
    const nextRank = getDisplayLabelRank(next);

    if (nextRank !== currentRank) return nextRank > currentRank ? next : current;
    return normalizeDisplayText(next).localeCompare(normalizeDisplayText(current), 'pt-BR') < 0 ? next : current;
};

const compareDisplayText = (a, b) => {
    const normalizedA = normalizeSectorFilterLabel(a);
    const normalizedB = normalizeSectorFilterLabel(b);
    const normalizedComparison = normalizedA.localeCompare(normalizedB, 'pt-BR');

    if (normalizedComparison !== 0) return normalizedComparison;
    return normalizeDisplayText(a).localeCompare(normalizeDisplayText(b), 'pt-BR');
};

export const buildSectorFilterGroups = (sectorOptions = []) => {
    if (!Array.isArray(sectorOptions)) return [];

    const groupsByKey = new Map();

    sectorOptions.forEach((option) => {
        const value = getOptionValue(option);
        const text = getOptionText(option);
        const labelSource = text || value;

        if (!value || isPlaceholderSector(value) || isPlaceholderSector(labelSource)) return;

        const canonical = getCanonicalSector(labelSource);
        if (!canonical.key || isPlaceholderSector(canonical.key)) return;

        if (!groupsByKey.has(canonical.key)) {
            groupsByKey.set(canonical.key, {
                key: canonical.key,
                label: canonical.label || labelSource,
                values: [],
                fixedLabel: canonical.fixedLabel,
            });
        } else if (!groupsByKey.get(canonical.key).fixedLabel) {
            const group = groupsByKey.get(canonical.key);
            group.label = chooseDisplayLabel(group.label, canonical.label || labelSource);
        }

        addUnique(groupsByKey.get(canonical.key).values, value);
    });

    return Array.from(groupsByKey.values())
        .filter((group) => group.values.length > 0)
        .map(({ fixedLabel, ...group }) => ({
            ...group,
            values: [...group.values].sort(compareDisplayText),
        }))
        .sort((a, b) => compareDisplayText(a.label, b.label) || a.key.localeCompare(b.key, 'pt-BR'));
};

const getSelectedSectorKey = (value) => getCanonicalSector(value).key;

export const isSectorGroupSelected = (selectedSectors = [], group) => {
    if (!group?.key || !Array.isArray(selectedSectors)) return false;

    const groupValueKeys = new Set(group.values.map(getSelectedSectorKey));
    return selectedSectors.some((sector) => {
        if (isPlaceholderSector(sector)) return false;
        const key = getSelectedSectorKey(sector);
        return key === group.key || groupValueKeys.has(key);
    });
};

export const toggleSectorGroupSelection = (selectedSectors = [], group) => {
    if (!group?.key || !Array.isArray(group.values)) return selectedSectors;

    const groupValueKeys = new Set(group.values.map(getSelectedSectorKey));
    const withoutGroup = selectedSectors.filter((sector) => {
        if (isPlaceholderSector(sector)) return false;
        const key = getSelectedSectorKey(sector);
        return key !== group.key && !groupValueKeys.has(key);
    });

    if (isSectorGroupSelected(selectedSectors, group)) return withoutGroup;

    const next = [...withoutGroup];
    group.values.forEach((value) => addUnique(next, value));
    return next;
};

export const expandSectorSelections = (selectedSectors = [], sectorOptions = []) => {
    if (!Array.isArray(selectedSectors)) return [];

    const groups = buildSectorFilterGroups(sectorOptions);
    const groupsByKey = new Map(groups.map((group) => [group.key, group]));
    const groupsByValueKey = new Map();

    groups.forEach((group) => {
        group.values.forEach((value) => {
            groupsByValueKey.set(getSelectedSectorKey(value), group);
        });
    });

    const expanded = [];
    selectedSectors.forEach((sector) => {
        const value = normalizeDisplayText(sector);
        if (!value || isPlaceholderSector(value)) return;

        const key = getSelectedSectorKey(value);
        const group = groupsByValueKey.get(key) || groupsByKey.get(key);

        if (group) {
            group.values.forEach((groupValue) => addUnique(expanded, groupValue));
            return;
        }

        addUnique(expanded, value);
    });

    return expanded;
};
